/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Ticket, Calendar, DollarSign, Upload, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../../ui/Button';
import { mintNFTTicket } from '../../../services/nft';

interface MintTicketModalProps {
    onClose: () => void;
    onSuccess: () => void;
    merchantAddress?: string;
}

export const MintTicketModal: React.FC<MintTicketModalProps> = ({ onClose, onSuccess, merchantAddress }) => {
    const [eventName, setEventName] = useState('');
    const [price, setPrice] = useState('');
    const [date, setDate] = useState('');
    const [asset, setAsset] = useState<'SOL'|'SUI'>('SOL');
    const [supply, setSupply] = useState('');
    const [isMinting, setIsMinting] = useState(false);
    const [completed, setCompleted] = useState(false);

    const handleMint = async () => {
        setIsMinting(true);
        try {
            await mintNFTTicket(
                merchantAddress || '0x...',
                asset,
                parseFloat(price),
                { name: eventName, date, supply: parseInt(supply) }
            );
            setCompleted(true);
            setTimeout(() => {
                onSuccess();
                onClose();
            }, 2000);
        } catch (e) {
            console.error(e);
        } finally {
            setIsMinting(false);
        }
    };

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
            <motion.div initial={{ scale: 0.9, y: 10 }} animate={{ scale: 1, y: 0 }} className="bg-white dark:bg-[#15171C] rounded-2xl shadow-2xl w-full max-w-lg p-6 relative border border-gray-200 dark:border-[#2D3142]">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"><X size={20}/></button>
                
                {completed ? (
                    <div className="text-center py-10">
                        <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
                        <h3 className="text-2xl font-serif font-bold text-white">Tickets Minted!</h3>
                        <p className="text-gray-400 mt-2">NFT Collection deployed to {asset} network.</p>
                    </div>
                ) : (
                    <>
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-2 bg-nobel-gold/10 rounded-lg text-nobel-gold"><Ticket size={24}/></div>
                            <h3 className="text-2xl font-serif font-bold text-gray-900 dark:text-white">Create NFT Tickets</h3>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Event Name</label>
                                <input type="text" value={eventName} onChange={e => setEventName(e.target.value)} className="w-full mt-1 p-3 bg-gray-50 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/10 outline-none text-black dark:text-white" placeholder="e.g. Summer Festival 2026"/>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Price ({asset})</label>
                                    <div className="relative mt-1">
                                        <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                                        <input type="number" value={price} onChange={e => setPrice(e.target.value)} className="w-full pl-8 p-3 bg-gray-50 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/10 outline-none text-black dark:text-white" placeholder="0.00"/>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Supply</label>
                                    <input type="number" value={supply} onChange={e => setSupply(e.target.value)} className="w-full mt-1 p-3 bg-gray-50 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/10 outline-none text-black dark:text-white" placeholder="1000"/>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Network</label>
                                    <select value={asset} onChange={e => setAsset(e.target.value as any)} className="w-full mt-1 p-3 bg-gray-50 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/10 outline-none text-black dark:text-white">
                                        <option value="SOL">Solana</option>
                                        <option value="SUI">Sui</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Date</label>
                                    <div className="relative mt-1">
                                        <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                                        <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full pl-8 p-3 bg-gray-50 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/10 outline-none text-black dark:text-white"/>
                                    </div>
                                </div>
                            </div>

                            <div className="p-6 border-2 border-dashed border-gray-200 dark:border-white/10 rounded-xl flex flex-col items-center justify-center text-gray-500 cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                                <Upload size={24} className="mb-2"/>
                                <span className="text-sm">Upload Ticket Artwork</span>
                            </div>
                        </div>

                        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-white/10">
                            <Button onClick={handleMint} disabled={isMinting || !eventName || !price} className="w-full" isLoading={isMinting}>
                                Deploy Ticket Contract
                            </Button>
                        </div>
                    </>
                )}
            </motion.div>
        </motion.div>
    );
};